'use client';

import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faPlus, faCheck, faSpinner, faTimes, faUserShield, faGear, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import { useRouter } from 'next/navigation';
import { useDispatch, useSelector } from 'react-redux';
import store, { RootState, AppDispatch } from '../../../store';
import { useAuth } from '../../../hooks/useAuth';
import { useAuthTranslation } from '../../language/hooks/useTranslation';
import { getInitials } from '../../shared/utils/stringUtils';
import { usePermissionGuard } from '../hooks/usePermissionGuard';
import Avatar from '../../../components/Avatar';
import { selectCurrentUser } from '../../../store/slices/userSlice';
import { resolveUserRoute } from '../../../utils/tenantRouting';

interface AccountSwitcherDropdownProps {
  buttonRect: DOMRect | null;
  isOpen: boolean;
  onClose: () => void;
  isDark: boolean;
  onOpenProfilePicture?: () => void;
}

const AccountSwitcherDropdown = React.memo<AccountSwitcherDropdownProps>(({
  buttonRect,
  isOpen,
  onClose,
  isDark,
  onOpenProfilePicture
}) => {
  const router = useRouter();
  const dispatch = useDispatch<AppDispatch>();
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { t } = useAuthTranslation();
  
  // Use centralized auth system for multi-account support
  const auth = useAuth();
  const isAuthenticated = auth.isAuthenticated;
  const user = auth.user;

  // Show admin panel iff the user holds any write-verb permission (verb-based
  // rule defined by isAdminPanelPermission). Read perms alone never unlock the
  // admin panel — see usePermissionGuard.ts for the exact rule.
  const { hasAdminPanelAccess } = usePermissionGuard();
  const hasAdminAccess = hasAdminPanelAccess;
  const availableAccounts = auth.availableAccounts;
  const reduxUser = useSelector((state: RootState) => selectCurrentUser(state));
  const currentAccountId = auth.currentAccountId;
  
  // Create compatibility secret object for existing UI
  const secret = user ? {
    userName: user.name || user.email || 'Unknown User',
    loginName: user.email || 'unknown@example.com'
  } : null;
  // Hydration safety
  const [isClient, setIsClient] = useState(false);
  
  // Local state for UI (simplified for new auth system)
  const [loading, setLoading] = useState(true);
  const [switching, setSwitching] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  const isOperationInProgress = switching !== null || removing !== null;

  // Use availableAccounts directly from useAuth hook instead of local state
  // This ensures immediate updates when accounts change
  const accounts = availableAccounts || [];

  // Debug logging removed for production

  // Mark as client-side after hydration
  useEffect(() => {
    setIsClient(true);
  }, []);

  // Load available accounts - use new auth system
  useEffect(() => {
    if (!isClient) return;
    
    const loadAccounts = async () => {
      try {
        setLoading(true);
        
        // Force refresh accounts and current account ID when dropdown opens
        await auth.refreshAccounts();
        
        setLoading(false);
      } catch (error) {
        console.error('Failed to load accounts:', error);
        setLoading(false);
      }
    };

    if (isOpen) {
      loadAccounts();
    }
  }, [isClient, isOpen, auth.refreshAccounts]); // Keep dependencies minimal to avoid loops



  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onClose]);

  const handleAccountSwitch = async (accountId: string) => {
    try {
      setSwitching(accountId);

      const success = await auth.switchAccount(accountId);

      if (success) {
        onClose();

        // Resolve the correct destination for the new user.
        // Uses shared utility — same logic as login/page.tsx.
        const freshUser = store.getState().auth.user;
        if (freshUser?.id) {
          const { destination } = await resolveUserRoute(dispatch, Number(freshUser.id), 'switch');
          router.push(destination);
        }
      }
    } catch (error) {
      console.error('Failed to switch account:', error);
    } finally {
      setSwitching(null);
    }
  };

  const handleMyInfo = () => {
    onClose();
    router.push('/myinfo');
  };

  const handleSettings = () => {
    onClose();
    router.push('/settings');
  };

  const handleAdmin = () => {
    onClose();
    router.push('/admin');
  };

  const handleAddAccount = () => {
    onClose();
    
    // Navigate to login page with add account parameter
    const currentUrl = window.location.href;
    router.push(`/login?addAccount=true&returnUrl=${encodeURIComponent(currentUrl)}`);
  };

  const handleRemoveAccount = async (accountId: string, event: React.MouseEvent) => {
    // Stop propagation to prevent account switch
    event.stopPropagation();
    
    try {
      setRemoving(accountId);
      
      // Use auth system for account removal
      const success = await auth.removeAccount(accountId);
      
      if (success) {
        // Force immediate refresh of accounts to ensure UI updates
        await auth.refreshAccounts();
      }
    } catch (error) {
      console.error('Failed to remove account:', error);
    } finally {
      setRemoving(null);
    }
  };

  const handleSignOut = async () => {
    try {
      onClose();
      
      // Use auth system logout
      const success = await auth.logout();
      if (success) {
        router.push('/login');
      } else {
        console.error('Logout failed');
        router.push('/login');
      }
    } catch (error) {
      console.error('Failed to sign out:', error);
      router.push('/login');
    }
  };

  // Memoize dropdown style before early return
  const dropdownStyle = React.useMemo(() => ({
    position: 'fixed' as const,
    top: buttonRect ? buttonRect.bottom + 12 : '60px',
    right: '16px',
    width: '320px',
    maxHeight: 'calc(100vh - 80px)',
    overflowY: 'auto' as const,
    zIndex: 999999999,
    backgroundColor: isDark ? '#1f2937' : '#ffffff',
    border: `1px solid ${isDark ? '#374151' : '#e5e7eb'}`,
    borderRadius: '8px',
    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
    pointerEvents: 'auto' as const
  }), [buttonRect, isDark]);

  // Don't render on server or during hydration
  if (!isClient || !isOpen) return null;

  return (
    <div
      ref={dropdownRef}
      style={dropdownStyle}
    >
      {/* Current User Info */}
      {isAuthenticated && secret && (
        <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-600">
          <div className="flex items-center space-x-3">
            <div className="relative group">
              <Avatar
                profilePictureUrl={reduxUser?.profilePictureUrl}
                displayName={reduxUser?.displayName ?? secret.userName}
                givenName={reduxUser?.givenName ?? undefined}
                surname={reduxUser?.surname ?? undefined}
                size="md"
              />
              {onOpenProfilePicture && (
                /* tablet-polish D2: avatar-overlay is the only way to change photo — show at rest on touch, hide-hover only on lg+ */
                <button
                  type="button"
                  onClick={onOpenProfilePicture}
                  className="absolute inset-0 rounded-full bg-black/40 opacity-100 lg:opacity-0 lg:group-hover:opacity-100 transition-opacity flex items-center justify-center"
                  title={t('changePhoto')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                </button>
              )}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {secret.userName}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                {secret.loginName}
              </p>
              {onOpenProfilePicture && (
                <button
                  type="button"
                  onClick={onOpenProfilePicture}
                  className="text-xs text-primary hover:underline mt-0.5"
                >
                  {t('changePhoto')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Available Accounts Section - Only show if there are multiple accounts */}
      {accounts.length > 1 && (
        <div className="border-b border-gray-200 dark:border-gray-600">
          <div className="px-4 py-2">
            <p className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wide">
              {t('switchAccount')}
            </p>
          </div>
          {accounts.map((account) => {
            // ROBUST: Use currentAccountId as the single source of truth for active account detection
            // Only the account with matching currentAccountId should show as active
            const isActive = currentAccountId && currentAccountId === account.id;
            
            const isRemoving = removing === account.id;
            const isSwitching = switching === account.id;
            const isDisabled = isOperationInProgress || isSwitching || isRemoving;
            
            return (
              <div
                key={account.id}
                className={`px-4 py-3 transition-colors duration-200 border-l-4 ${
                  isActive 
                    ? 'bg-primary/10 border-primary' // Active account with colored border
                    : 'hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer border-transparent' // Inactive accounts with transparent border for alignment
                } ${isDisabled ? 'opacity-50' : ''}`}
              >
                <div className="flex items-center justify-between w-full">
                  {/* Left side: Avatar and user info */}
                  <div 
                    className={`flex items-center space-x-3 flex-1 min-w-0 ${isActive ? 'cursor-default' : 'cursor-pointer'}`}
                    onClick={() => !isActive && !isDisabled && handleAccountSwitch(account.id)}
                  >
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-semibold text-xs flex-shrink-0 ${
                      isActive ? 'bg-primary' : 'bg-gray-400'
                    }`}>
                      {getInitials(account.userName)}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        isActive ? 'text-primary dark:text-primary' : 'text-gray-900 dark:text-white'
                      }`}>
                        {account.userName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                        {account.email}
                      </p>
                    </div>
                  </div>
                  
                  {/* Right side: Icons - always positioned at far right */}
                  <div className="flex items-center justify-center w-6 h-6 flex-shrink-0">
                    {switching === account.id ? (
                      <FontAwesomeIcon 
                        icon={faSpinner} 
                        className="text-gray-400 w-4 h-4 animate-spin" 
                      />
                    ) : isActive ? (
                      <FontAwesomeIcon 
                        icon={faCheck} 
                        className="text-primary w-4 h-4" 
                      />
                    ) : (
                      /* Remove button for inactive accounts */
                      accounts.length > 1 && (
                        <button
                          onClick={(e) => handleRemoveAccount(account.id, e)}
                          disabled={isDisabled}
                          className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors duration-200 flex items-center justify-center w-6 h-6"
                          title={t('removeAccount')}
                        >
                          {isRemoving ? (
                            <FontAwesomeIcon 
                              icon={faSpinner} 
                              className="text-red-500 w-3.5 h-3.5 animate-spin" 
                            />
                          ) : (
                            <FontAwesomeIcon 
                              icon={faTimes} 
                              className="text-gray-400 hover:text-red-500 w-3.5 h-3.5" 
                            />
                          )}
                        </button>
                      )
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Add Account */}
      <div
        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 flex items-center space-x-3"
        onClick={handleAddAccount}
      >
        <div className="w-5 h-5 flex items-center justify-center">
          <FontAwesomeIcon icon={faPlus} className="text-gray-600 dark:text-gray-400" />
        </div>
        <span className="text-sm text-gray-700 dark:text-gray-300">{t('addAccount')}</span>
      </div>

      {/* Admin Panel - Only show for users with admin roles */}
      {hasAdminAccess && (
        <div
          className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 flex items-center space-x-3"
          onClick={handleAdmin}
        >
          <FontAwesomeIcon icon={faUserShield} className="text-gray-600 dark:text-gray-400 w-5" />
          <span className="text-sm text-gray-700 dark:text-gray-300">{t('adminPanel')}</span>
        </div>
      )}

      {/* My Info */}
      <div
        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 flex items-center space-x-3"
        onClick={handleMyInfo}
      >
        <FontAwesomeIcon icon={faUserCircle} className="text-gray-600 dark:text-gray-400 w-5" />
        <span className="text-sm text-gray-700 dark:text-gray-300">{t('myInfo')}</span>
      </div>

      {/* Settings */}
      <div
        className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors duration-200 flex items-center space-x-3"
        onClick={handleSettings}
      >
        <FontAwesomeIcon icon={faGear} className="text-gray-600 dark:text-gray-400 w-5" />
        <span className="text-sm text-gray-700 dark:text-gray-300">{t('settings')}</span>
      </div>

      {/* Sign Out */}
      <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-600">
        <button
          className="w-full px-4 py-2.5 text-sm rounded-full font-semibold bg-gradient-to-r from-primary to-accent text-white shadow-md hover:shadow-lg transform hover:-translate-y-0.5 transition-all duration-300 flex items-center justify-center space-x-2"
          onClick={handleSignOut}
        >
          <FontAwesomeIcon icon={faSignOutAlt} className="w-4 h-4" />
          <span>
            {accounts.length > 1 ? t('signOutAll') : t('logout')}
          </span>
        </button>
      </div>

      {/* Loading State */}
      {loading && (
        <div className="px-4 py-3 text-center">
          <p className="text-sm text-gray-500 dark:text-gray-400">{t('loadingAccounts')}</p>
        </div>
      )}

      {/* No Additional Accounts - Only show when there's exactly one account */}
      {!loading && accounts.length === 1 && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {t('noAdditionalAccounts')}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1">
            {t('useAddAccountToSignIn')}
          </p>
        </div>
      )}

      {/* No Accounts Found - Only show when there are no accounts at all */}
      {!loading && accounts.length === 0 && (
        <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-600">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            {t('noAccountsFound')}
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-1">
            {t('useAddAccountToSignIn')}
          </p>
        </div>
      )}
    </div>
  );
});

AccountSwitcherDropdown.displayName = 'AccountSwitcherDropdown';

export default AccountSwitcherDropdown;